// Per-user gamification summary persisted at:
//   users/{userId}/gamification/summary
//
// Shape is intentionally flat — UI surfaces (XPCounter, StreakBadge,
// AchievementToast, ProgressDashboard) live-subscribe to this single doc.
// Per-action history is stored in users/{userId}/gamification/actions for
// the streak calendar (date-bucketed XP) without bloating the summary.

export interface AchievementRecord {
  id: string;
  earnedAt: string; // ISO
  xpAwarded: number;
}

export interface StreakFreezeState {
  /** Total freezes consumed across the lifetime — informational. */
  totalUsed: number;
  /** Freezes consumed in the current calendar month. Reset when monthAnchor rolls over. */
  usedThisMonth: number;
  /** YYYY-MM the usedThisMonth counter belongs to. When this differs from
   *  the current month at read-time, callers reset usedThisMonth to 0. */
  monthAnchor: string | null;
}

export interface UserGamification {
  userId: string;
  totalXp: number;
  currentStreakDays: number;
  longestStreakDays: number;
  /** Streak length the moment before the current streak broke. Used by the
   *  recovery banner ("You were on a 14-day streak — start a new one today"). */
  previousStreakDays: number;
  /** Local-day ISO string (YYYY-MM-DD) of the last day that contributed
   *  to the streak. */
  lastActiveDate: string | null;
  achievements: AchievementRecord[];
  streakFreeze: StreakFreezeState;

  // ─── Counters used by achievement evaluation ───
  totalNodesCompleted: number;
  pathsCompleted: number;
  fullRecalibrationsCompleted: number;
  consecutivePerfectCheckpoints: number;
  checkpointsPassedCount: number;
  goDeeperModeCount: number;
  testMeModeCount: number;
  /** Subject domains uploaded (deduped). For Polyglot. */
  subjectsUploaded: string[];
  diagnosticCompleted: boolean;

  // ─── Phase-2 placeholders — populated when §11.3/§11.4 ship. ───
  voiceTutorSessionsCount: number;
  knowledgeGraphNodesExplored: number;
  contradictionsFound: number;

  createdAt: string;
  updatedAt: string;
}

export const EMPTY_GAMIFICATION: Omit<UserGamification, 'userId' | 'createdAt' | 'updatedAt'> = {
  totalXp: 0,
  currentStreakDays: 0,
  longestStreakDays: 0,
  previousStreakDays: 0,
  lastActiveDate: null,
  achievements: [],
  streakFreeze: {
    totalUsed: 0,
    usedThisMonth: 0,
    monthAnchor: null,
  },
  totalNodesCompleted: 0,
  pathsCompleted: 0,
  fullRecalibrationsCompleted: 0,
  consecutivePerfectCheckpoints: 0,
  checkpointsPassedCount: 0,
  goDeeperModeCount: 0,
  testMeModeCount: 0,
  subjectsUploaded: [],
  diagnosticCompleted: false,
  voiceTutorSessionsCount: 0,
  knowledgeGraphNodesExplored: 0,
  contradictionsFound: 0,
};

// ─── Levels (spec §11.5.10) ───

export interface LevelDefinition {
  level: number;
  title: string;
  /** Minimum XP required to enter this level. */
  threshold: number;
}

export const LEVELS: LevelDefinition[] = [
  { level: 1, title: 'Novice',     threshold: 0 },
  { level: 2, title: 'Explorer',   threshold: 500 },
  { level: 3, title: 'Learner',    threshold: 1500 },
  { level: 4, title: 'Scholar',    threshold: 3500 },
  { level: 5, title: 'Specialist', threshold: 7500 },
  { level: 6, title: 'Expert',     threshold: 15000 },
  { level: 7, title: 'Master',     threshold: 30000 },
  { level: 8, title: 'Sage',       threshold: 60000 },
];

export interface LevelInfo {
  current: LevelDefinition;
  next: LevelDefinition | null;
  xpIntoLevel: number;
  xpToNextLevel: number;
  /** 0..1 progress toward the next level; 1 if max level. */
  progress: number;
}

export function levelFromXp(totalXp: number): LevelInfo {
  let current = LEVELS[0];
  for (const lvl of LEVELS) {
    if (totalXp >= lvl.threshold) current = lvl;
    else break;
  }
  const next = LEVELS.find((l) => l.level === current.level + 1) ?? null;
  if (!next) {
    return { current, next: null, xpIntoLevel: totalXp - current.threshold, xpToNextLevel: 0, progress: 1 };
  }
  const span = next.threshold - current.threshold;
  const xpIntoLevel = totalXp - current.threshold;
  return {
    current,
    next,
    xpIntoLevel,
    xpToNextLevel: next.threshold - totalXp,
    progress: span <= 0 ? 1 : Math.max(0, Math.min(1, xpIntoLevel / span)),
  };
}

// ─── Per-day action log (for ProgressDashboard streak calendar) ───
//
// Stored at users/{userId}/gamification/actions/{YYYY-MM-DD} so the
// dashboard can render a GitHub-style heatmap without scanning all
// gamification writes.

export interface DailyActivityRecord {
  date: string; // YYYY-MM-DD
  xpEarned: number;
  actionCounts: Record<string, number>;
  updatedAt: string;
}
